import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Icon } from "../icon/icon";
import classNames from "classnames";
import { Button,Tooltip} from "antd";
import "./main-navigation.css";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileBar from "../profile-bar/profile-bar";
import { useSelector } from "react-redux";
import { selectCurrentProject } from "../../store/slices/projects";
import { useAccess } from "../../common/utils/usePermissions";
import { canOpenSection } from "../../common/permissions/screens";
import { ProjectPickerModal } from "../../modules/administration/project-management/project-picker";
import { darken } from "../../common/utils/color";
import { SearchOutlined } from "@ant-design/icons";
import ContentSearchModal from "../content-search/content-search-modal";
import { SPOTLIGHT_TYPES, resultPathOf } from "../content-search/content-types";

// LF360-2069: the shortcut as the user's keyboard names it.
const SEARCH_SHORTCUT = typeof navigator !== "undefined" && /Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent)
  ? "⌘K"
  : "Ctrl+K";

// `testID`: data-testid of the button, for the browser tests (LF360-1981); the
// icon-only buttons have no other name than their hover tooltip.
const NavItem = ({ icon, href, disable, title, testID }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isActive = pathname.includes(href);

  if (disable) {
    return null;
  }

  const handleClick = () => {
    navigate(href, { state: { openSidebar: true } });
  };

  return (
    <Tooltip title={title} placement={"right"} arrow={false} style={{background: "white"}}>
      <Button
        type={isActive ? "default" : "text"}
        onClick={handleClick}
        icon={<Icon name={icon} />}
        data-testid={testID}
      />
    </Tooltip>
  );
};

export const MainNavigation = memo(() => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  // LF360-2024: a section shows when the user may open any of its screens.
  const access = useAccess();
  const canSeeAdministration = canOpenSection(access, "/administration");
  const canSeeReports = canOpenSection(access, "/analytics");
  const canSeeContent = canOpenSection(access, "/content");
  const canSeeSubjects = access.isOrgAdmin || access.permissions.includes("subjects.view");
  const canSearch = access.ready && (canSeeContent || canSeeSubjects || canSeeAdministration);
  const searchTypes = useMemo(() => ["all", ...SPOTLIGHT_TYPES.filter((type) => (
    type === "subjects" ? canSeeSubjects : type === "settings" ? canSeeAdministration : canSeeContent
  ))], [canSeeSubjects, canSeeAdministration, canSeeContent]);
  const project = useSelector(selectCurrentProject);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const bg = project?.color || "#A3F1CB";
  const text = darken(bg, 0.35);
  const code = project?.code || "";
  const spotlightType = useMemo(() => ([
    ["/content/articles", "articles"], ["/content/fitfacts", "fitFacts"],
    ["/content/tasks", "tasks"], ["/content/plans", "plans"],
    ["/content/lifepoints", "lifePoints"], ["/content/categories", "categories"],
    ["/content/tags", "tags"], ["/content/scenarios", "scenarios"],
    ["/content/onboardings", "onboardings"], ["/content/rules", "rules"],
    ["/subject-management", "subjects"], ["/administration", "settings"],
  ].find(([path]) => pathname.includes(path))?.[1] || "all"), [pathname]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (!canSearch) {return;}
      if ((!event.metaKey && !event.ctrlKey) || event.key.toLowerCase() !== "k") {return;}
      // Works from a field too, as Spotlight does elsewhere; only a rich text
      // editor keeps the keys, since Cmd/Ctrl+K there makes a link.
      if (event.target?.isContentEditable) {return;}
      event.preventDefault();
      setSpotlightOpen(true);
    };
    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [canSearch]);

  // The widget hands over each result with its type (the tab it was found in).
  const openSearchResult = useCallback((item) => {
    setSpotlightOpen(false);
    navigate(`/${code}${resultPathOf(item)}`);
  }, [code, navigate]);

  const onProjectBadgeClick = useCallback(() => {
    setPickerOpen(true);
  }, [project]);

  const handleCreateNew = useCallback(() => {
    console.log("Create new project clicked");
    // TODO
  }, []);

  return (
    <div className={classNames("lf-main-navigation")} data-testid="main-navigation">
      <div className={"lf-main-navigation__header"}>
        <img
          className="lf-main-navigation__logo"
          src={`${
            process.env.REACT_APP_DEV_URL || document.location.origin
          }/api/images/logo.svg`}
          alt="logo"
        />
      </div>
      <div className="lf-main-navigation__project">
        <div
          className="lf-main-navigation__project-badge"
          style={{ backgroundColor: bg, color: text }}
          onClick={onProjectBadgeClick}
          role="button"
          aria-label={`Current project ${project?.title || ""}`}
          title={project?.title || "Project"}
          data-testid="project-badge"
        >
          {code}
        </div>
      </div>
      <div className={"lf-main-navigation__body lf-main-navigation__content"}>
        {canSearch && (
          <Tooltip title={`Search content (${SEARCH_SHORTCUT})`} placement="right" arrow={false}>
            <Button type="text" onClick={() => setSpotlightOpen(true)} icon={<SearchOutlined />} aria-label="Search content" />
          </Tooltip>
        )}
        <NavItem icon={"dashboard"} href={`/${code}/dashboard`} title={'Dashboard'} testID="nav-dashboard"/>
        <NavItem icon={"user"} href={`/${code}/subject-management`} title={'Subject management'} testID="nav-subjects"/>
        <NavItem
          icon={"reports"}
          href={`/${code}/analytics`}
          disable={!canSeeReports}
          title={"Analytics"}
          testID="nav-analytics"
        />
        <NavItem
          icon={"content"}
          href={`/${code}/content`}
          disable={!canSeeContent}
          title={"Content"}
          testID="nav-content"
        />
      </div>
      <div className={"lf-main-navigation__body lf-main-navigation__settings"}>
        <NavItem
          icon={"setting"}
          href={`/${code}/administration`}
          disable={!canSeeAdministration}
          title={"Administration"}
          testID="nav-administration"
        />
      </div>
      <div className={"lf-main-navigation__footer"}>
        <ProfileBar />
      </div>
      <ProjectPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onCreateNew={handleCreateNew}
      />
      <ContentSearchModal
        key={`${project?.id}-${access.permissions.join(",")}-${access.isOrgAdmin}`}
        access={access}
        open={spotlightOpen}
        onClose={() => setSpotlightOpen(false)}
        onOpenItem={openSearchResult}
        browse
        multiple={false}
        types={searchTypes}
        defaultType={spotlightType}
      />
    </div>
  );
});
